import {
  createBrowserRouter,
  Outlet,
  RouteObject,
  ScrollRestoration,
} from 'react-router-dom';
import Landing from '../pages/Landing';
import ModalDetail from '@/pages/PostDetail';
import Navbar from '@/components/Navbar';
import Profile from '@/pages/Profile';
import PrivateRoute from '@/components/PrivateRoute';
import PreviewModalDetail from '@/pages/Preview';
import PostUpload from '@/pages/PostUpload';
import Footer from '@/components/Footer';
import Signup from '@/pages/Signup';
import RouteWrapper from '@/components/RouteWrapper';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import IconLegend from '@/pages/IconLegend';
import NotFound from '@/pages/NotFound';
import ResetPassword from '@/pages/ResetPassword';
import ChangePassword from '@/pages/ChangePassword';
import ErrorBoundary from '@/pages/ErrorBoundary';
import ProjectGroupDetail from '@/pages/ProjectGroupDetail';
import ProjectGroupCreate from '@/pages/ProjectGroupCreate';
import ProjectGroupAdminPreview from '@/pages/ProjectGroupAdminPreview';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/Blog/BlogPost';
import UploadV2Page from '@/pages/UploadV2';
import GroupEditRoute from '@/pages/UploadV2/GroupEditRoute';

const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RouteWrapper>
        <Navbar surface="feed" />
        <Landing />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/model/:id',
    element: (
      <RouteWrapper>
        <Navbar />
        <ModalDetail />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/model/preview/',
    element: (
      <RouteWrapper>
        <Navbar />
        <PreviewModalDetail />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/blog',
    element: (
      <RouteWrapper>
        <Navbar />
        <Blog />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/blog/:slug',
    element: (
      <RouteWrapper>
        <Navbar />
        <BlogPost />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/:username',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <Navbar surface="profile" />
          <Profile />
        </PrivateRoute>
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project/create',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <PostUpload />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project/edit/:postId',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <PostUpload />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/changePassword',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <Navbar />
          <ChangePassword />
          <Footer />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/signup',
    element: <Signup />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/resetPassword',
    element: (
      <>
        <Navbar />
        <ResetPassword />
        <Footer />
      </>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/privacyPolicy',
    element: (
      <>
        <Navbar />
        <PrivacyPolicy />
        <Footer />
      </>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/legend',
    element: (
      <>
        <Navbar />
        <IconLegend />
        <Footer />
      </>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project-groups/create',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <ProjectGroupCreate />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project-groups/preview/',
    element: (
      <RouteWrapper>
        <Navbar />
        <ProjectGroupAdminPreview />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project-groups/:id',
    element: (
      <RouteWrapper>
        <Navbar />
        <ProjectGroupDetail />
        <Footer />
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project/v2/upload',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <UploadV2Page />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project/v2/edit/:postId',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <UploadV2Page />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/project/v2/group/:groupId',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <UploadV2Page />
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    // Its own path, not a branch inside the wizard route: the wizard redirects
    // itself to /group/:groupId mid-authoring, and anything that can render
    // something else there unmounts the live session.
    path: '/project/v2/group/:groupId/edit',
    element: (
      <RouteWrapper>
        <PrivateRoute>
          <div className="h-screen bg-card">
            <GroupEditRoute />
          </div>
        </PrivateRoute>
      </RouteWrapper>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: '*',
    element: <NotFound />,
    errorElement: <ErrorBoundary />,
  },
];

const routerRoutes: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    {
      element: (
        <>
          <ScrollRestoration />
          <Outlet />
        </>
      ),
      errorElement: <ErrorBoundary />,
      children: appRoutes,
    },
  ]);

export default routerRoutes;
